import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, MapPin, ShieldCheck, Rocket, Sparkles, FileText, Users, Star, Building2 } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import JobCard from "@/components/JobCard";
import { useAuth } from "@/context/AuthContext";

const AGE_BUCKETS = [
  { label: "Ages 13–14", color: "bg-[var(--nh-mint)]", age: 14 },
  { label: "Age 15", color: "bg-[var(--nh-sky)]", age: 15 },
  { label: "Ages 16–17", color: "bg-[var(--nh-accent)]", age: 17 },
  { label: "Ages 18–21", color: "bg-[var(--nh-pink)]", age: 20 },
];

const CATEGORIES = [
  { key: "retail", label: "Retail" },
  { key: "food service", label: "Food Service" },
  { key: "tech", label: "Tech" },
  { key: "nonprofit", label: "Nonprofit" },
  { key: "healthcare", label: "Healthcare" },
  { key: "sports", label: "Sports" },
  { key: "entertainment", label: "Entertainment" },
  { key: "education", label: "Education" },
];

export default function Home() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [where, setWhere] = useState("");
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/jobs", { params: { limit: 6 } }).then((r) => setFeatured(r.data.jobs || []));
  }, []);

  const doSearch = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (where) p.set("location", where);
    nav(`/jobs?${p.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 nh-dots opacity-40 pointer-events-none"/>
        <div className="nh-container py-14 md:py-20 grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 nh-tag bg-[var(--nh-mint)]">
              <ShieldCheck size={12}/> Safe. Youth-first. Free.
            </div>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-black font-display leading-[0.95]">
              Find Jobs You <span className="bg-[var(--nh-accent)] px-2 -mx-1 inline-block border-2 border-black rounded-md">Actually</span> Qualify For
            </h1>
            <p className="mt-5 text-lg text-[var(--nh-muted)] max-w-xl">
              Real opportunities for teens 13–21 — filtered by your age, skills, and schedule. Build a resume, land your first shift, and share what you learn.
            </p>

            <form onSubmit={doSearch} className="mt-8 grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-6 flex items-center bg-white border-2 border-black rounded-full pl-4 shadow-[4px_4px_0_0_var(--nh-ink)]">
                <Search size={18}/>
                <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Barista, tutor, intern…"
                  className="flex-1 px-3 py-3 outline-none font-medium bg-transparent" data-testid="home-search-q"/>
              </div>
              <div className="sm:col-span-4 flex items-center bg-white border-2 border-black rounded-full pl-4 shadow-[4px_4px_0_0_var(--nh-ink)]">
                <MapPin size={18}/>
                <input value={where} onChange={(e)=>setWhere(e.target.value)} placeholder="City or Remote"
                  className="flex-1 px-3 py-3 outline-none font-medium bg-transparent" data-testid="home-search-location"/>
              </div>
              <button type="submit" className="nh-btn nh-btn-primary sm:col-span-2" data-testid="home-search-submit">
                Search
              </button>
            </form>

            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="nh-tag bg-white">Age-based auto-filter</span>
              <span className="nh-tag bg-white">Work permit friendly</span>
              <span className="nh-tag bg-white">School schedules</span>
              <span className="nh-tag bg-white">Remote OK</span>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative nh-card p-0 overflow-hidden">
              <img src="https://images.unsplash.com/photo-1559924835-8393ef3ba676?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                   alt="Diverse teens ready to work" className="w-full h-[420px] object-cover"/>
            </div>
            <div className="absolute -top-4 -left-4 hidden md:block">
              <div className="nh-card bg-[var(--nh-primary)] text-white px-4 py-2 rotate-[-6deg]">
                <div className="text-xs uppercase font-bold tracking-widest">Ages 15+</div>
                <div className="text-lg font-display font-black">Approved ✦</div>
              </div>
            </div>
            <div className="absolute -bottom-5 -right-2">
              <div className="nh-card bg-[var(--nh-accent)] px-4 py-2 rotate-[4deg]">
                <div className="text-xs uppercase font-bold tracking-widest">2,400+</div>
                <div className="text-lg font-display font-black">Youth Placed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="nh-marquee" data-testid="home-marquee">
        <div className="nh-marquee__track">
          {Array(2).fill(0).map((_,i)=>(
            <div key={i} className="flex gap-12">
              <span>★ Adzuna</span><span>★ Remotive</span><span>★ USAJobs</span>
              <span>★ Safe-by-Design</span><span>★ Work Permit Friendly</span>
              <span>★ Free for Youth</span><span>★ Free for Employers</span>
              <span>★ Parental-Consent Aware</span>
            </div>
          ))}
        </div>
      </div>

      {/* Age buckets */}
      <section className="nh-container py-16">
        <h2 className="font-display font-black text-3xl sm:text-4xl">Browse by Age</h2>
        <p className="text-[var(--nh-muted)] mt-2">Every job is tagged by legal age eligibility. You’ll only see what you can actually apply for.</p>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {AGE_BUCKETS.map((b) => (
            <Link key={b.label} to={`/jobs?age=${b.age}`} className="nh-card p-6" data-testid={`age-bucket-${b.age}`}>
              <div className={`h-14 w-14 rounded-lg border-2 border-black flex items-center justify-center ${b.color}`}>
                <Sparkles/>
              </div>
              <h3 className="font-display font-black text-xl mt-3">{b.label}</h3>
              <p className="text-sm text-[var(--nh-muted)] mt-1">See jobs legally open at this age.</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Popular categories */}
      <section className="nh-container py-10">
        <h2 className="font-display font-black text-3xl sm:text-4xl">Popular Categories</h2>
        <div className="mt-6 flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <Link key={c.key} to={`/jobs?industry=${encodeURIComponent(c.key)}`}
                  className="nh-btn nh-btn-ghost" data-testid={`cat-${c.key.replace(/\s/g,'-')}`}>
              {c.label}
            </Link>
          ))}
        </div>
      </section>

      {/* Latest jobs */}
      <section className="nh-container py-10">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-black text-3xl sm:text-4xl">Fresh Opportunities</h2>
          <Link to="/jobs" className="nh-btn nh-btn-outline text-xs">View all</Link>
        </div>
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((j)=> <JobCard key={j.id} job={j}/>)}
        </div>
      </section>

      {/* Resume CTA */}
      <section className="nh-container py-16">
        <div className="nh-card bg-[var(--nh-accent)] p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="nh-tag bg-black text-white border-white"><FileText size={12} className="mr-1"/> Resume Builder</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-3">No experience? No problem.</h2>
            <p className="mt-3 text-black/80 max-w-md">Our AI helps you draft a professional objective, list school activities as skills, and export a clean PDF in minutes — all free.</p>
            <Link to="/resume-builder" className="nh-btn nh-btn-blue mt-6 inline-flex" data-testid="cta-resume">
              Build My First Resume
            </Link>
          </div>
          <div className="nh-card bg-white p-4">
            <img src="https://images.unsplash.com/photo-1704770064557-416292d1f4bd?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                 alt="Teen building a resume" className="rounded-md w-full h-64 object-cover"/>
          </div>
        </div>
      </section>

      {/* Employer CTA */}
      <section className="nh-container py-4">
        <div className="nh-card bg-[var(--nh-secondary)] text-white p-8 md:p-12 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="nh-tag bg-white text-black"><Building2 size={12} className="mr-1"/> Local Businesses · Churches · Nonprofits · Cities · School Districts</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-3">Post free jobs. Reach youth ready to work.</h2>
            <p className="mt-3 text-white/85 max-w-xl">Local businesses, churches, nonprofits, cities, counties, and school districts can post opportunities free forever. Verified organizations get a shield. All posts moderated for safety.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/signup?role=employer" className="nh-btn nh-btn-secondary" data-testid="cta-employer">Post a Job — Free</Link>
          </div>
        </div>
      </section>

      {/* Federal Programs */}
      <section className="nh-container py-10">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <span className="nh-tag bg-[var(--nh-mint)]">Federal Programs</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl mt-2">Beyond jobs — training, service, apprenticeships.</h2>
            <p className="text-[var(--nh-muted)] mt-2 max-w-xl">Explore free federal programs to build your future — training, service years, and paid apprenticeships.</p>
          </div>
          <Link to="/resources" className="nh-btn nh-btn-outline text-xs">All Resources</Link>
        </div>
        <div className="mt-6 grid md:grid-cols-4 gap-4">
          {[
            { n: "Apprenticeship.gov", d: "Paid apprenticeships nationwide.", c: "bg-[var(--nh-accent)]" },
            { n: "AmeriCorps", d: "Service with education award.", c: "bg-[var(--nh-sky)]" },
            { n: "YouthBuild", d: "Learn construction + finish HSE.", c: "bg-[var(--nh-mint)]" },
            { n: "Job Corps", d: "Free training, ages 16–24.", c: "bg-[var(--nh-pink)]" },
          ].map((r,i)=>(
            <Link key={i} to="/resources" className={`nh-card p-5 ${r.c}`} data-testid={`prog-${i}`}>
              <div className="font-display font-black text-lg">{r.n}</div>
              <div className="text-xs mt-2">{r.d}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Success stories */}
      <section className="nh-container py-16">
        <h2 className="font-display font-black text-3xl sm:text-4xl">Youth Success Stories</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {[
            { name: "Maya, 16", job: "Barista @ Beanery", quote: "Landed my first job in 4 days. The resume builder wrote my objective for me." },
            { name: "Devon, 14", job: "Pet Walker", quote: "Made $180 my first weekend. Parents approved the app before I signed up." },
            { name: "Priya, 18", job: "SWE Intern", quote: "Remote internship, real code, real mentor. Best summer ever." },
          ].map((s,i)=>(
            <div key={i} className="nh-card p-6" data-testid={`story-${i}`}>
              <div className="flex gap-1 text-[var(--nh-primary)]">{[...Array(5)].map((_,k)=><Star key={k} size={16} fill="currentColor" strokeWidth={0}/>)}</div>
              <p className="mt-3 text-lg font-display leading-snug">“{s.quote}”</p>
              <div className="mt-4 flex items-center gap-2 text-sm">
                <div className="h-8 w-8 rounded-full bg-[var(--nh-mint)] border-2 border-black flex items-center justify-center font-black">{s.name[0]}</div>
                <span className="font-bold">{s.name}</span>
                <span className="text-[var(--nh-muted)]">— {s.job}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Safety */}
      <section className="nh-container py-4">
        <div className="nh-card p-8 md:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="nh-tag bg-[var(--nh-mint)]"><ShieldCheck size={12} className="mr-1"/> Safe by design</div>
            <h2 className="font-display font-black text-3xl mt-3">Your safety is the priority.</h2>
            <ul className="mt-4 space-y-2 text-sm text-[var(--nh-muted)]">
              <li>✓ We never show your phone/email publicly</li>
              <li>✓ Every job is moderated by real humans</li>
              <li>✓ Report or block any user or post in one tap</li>
              <li>✓ Parental consent language for under-18</li>
            </ul>
            <div className="mt-6 flex gap-3">
              <Link to="/legal/safety" className="nh-btn nh-btn-outline text-xs">Read Youth Safety</Link>
              <Link to="/legal/scams" className="nh-btn nh-btn-ghost text-xs">Scam Warnings</Link>
            </div>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1524069290683-0457abfe42c3?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                 alt="Community of teens" className="w-full h-64 object-cover rounded-lg border-2 border-black"/>
            <div className="absolute -bottom-4 -right-4 nh-card bg-[var(--nh-primary)] text-white px-4 py-2 rotate-[3deg]">
              <div className="text-xs uppercase font-bold">Moderated</div>
              <div className="text-lg font-display font-black">Community</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
